import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  bulkAssignTag,
  createTagGroup,
  createTagValue,
  deleteTagGroup,
  deleteTagValue,
  listCustomerTagCandidates,
  listTagGroups,
} from '../../services/tagsApi';
import { useSession } from '../auth/SessionProvider';
import './Admin.css';

/*
 * Tag administration.
 *
 * A tag group is a dimension ("Trade channel") and a value is one of its
 * answers ("Swimart"). A group declares which entities it targets, which is
 * what keeps one vocabulary usable on both customers and products without
 * "Customer segment" quietly becoming a product field.
 *
 * The bulk panel exists because the source of truth for a franchise list is a
 * vendor spreadsheet, not Epicor: paste the codes, see which ones are not
 * entities here, then tag the ones that are. Customer keys are (company,
 * cust_num) because CustNum is the immutable ERP key; the banner search is a
 * suggestion list to confirm, never an automatic rule.
 */

const ENTITIES = [
  { id: 'customer', label: 'Customers' },
  { id: 'product', label: 'Products' },
];

const slug = (text) =>
  (text || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);

const entityLabel = (id) => ENTITIES.find((e) => e.id === id)?.label || id;

export default function Tags() {
  const { isAdmin } = useSession();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const [newGroup, setNewGroup] = useState({ label: '', code: '', entity_types: ['customer', 'product'] });
  const [newValue, setNewValue] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setGroups(await listTagGroups({ with_counts: true }));
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load tags.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) load();
    else setLoading(false);
  }, [isAdmin, load]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  if (!isAdmin) {
    return (
      <div className="adm-page">
        <div className="apr-alert warn">
          Tag administration needs the admin role. Sign in with an administrator account.
        </div>
      </div>
    );
  }

  const addGroup = async (e) => {
    e.preventDefault();
    const label = newGroup.label.trim();
    const code = (newGroup.code.trim() || slug(label));
    if (!label || !code || newGroup.entity_types.length === 0) return;
    try {
      await createTagGroup({ code, label, entity_types: newGroup.entity_types });
      setNewGroup({ label: '', code: '', entity_types: ['customer', 'product'] });
      setToast(`Tag group “${label}” created. Add its values below.`);
      await load();
    } catch (e2) {
      setError(e2?.response?.data?.detail || 'Could not create that tag group.');
    }
  };

  const removeGroup = async (group) => {
    const assigned = (group.values || []).reduce(
      (n, v) => n + Number(v.customer_count || 0) + Number(v.product_count || 0),
      0,
    );
    const confirmMsg = assigned
      ? `Delete “${group.label}”? This removes ${group.values.length} tag(s) from ${assigned} entit${assigned === 1 ? 'y' : 'ies'}.`
      : `Delete “${group.label}”?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await deleteTagGroup(group.id, true);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not delete that group.');
    }
  };

  const addValue = async (group, e) => {
    e.preventDefault();
    const draft = newValue[group.id] || {};
    const label = (draft.label || '').trim();
    const code = (draft.code || '').trim() || slug(label);
    if (!label || !code) return;
    try {
      await createTagValue(group.id, { code, label });
      setNewValue((prev) => ({ ...prev, [group.id]: {} }));
      await load();
    } catch (e2) {
      setError(e2?.response?.data?.detail || 'Could not add that tag.');
    }
  };

  const removeValue = async (group, value) => {
    const assigned = Number(value.customer_count || 0) + Number(value.product_count || 0);
    const confirmMsg = assigned
      ? `Delete “${value.label}”? It is assigned to ${assigned} entit${assigned === 1 ? 'y' : 'ies'}, which will lose the tag.`
      : `Delete “${value.label}”?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await deleteTagValue(value.id, true);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not delete that tag.');
    }
  };

  return (
    <div className="adm-page">
      <header className="oc-header">
        <div className="oc-header-text">
          <h1>Tags</h1>
          <p>
            One labelling vocabulary for the whole console. A group is the question, its values are
            the answers, and each group decides which entities it can label — so “Swimart” can mark
            both the franchise’s customers and the products sold to them.
          </p>
        </div>
      </header>

      {error && <div className="apr-alert bad">{error}</div>}
      {toast && <div className="apr-alert ok">{toast}</div>}

      <form className="adm-newrow" onSubmit={addGroup}>
        <input
          className="oc-input"
          placeholder="Group name, e.g. Trade channel"
          value={newGroup.label}
          onChange={(e) => setNewGroup({ ...newGroup, label: e.target.value })}
        />
        <input
          className="oc-input"
          placeholder={slug(newGroup.label) || 'code (auto)'}
          value={newGroup.code}
          onChange={(e) => setNewGroup({ ...newGroup, code: e.target.value })}
        />
        <span className="adm-tag-entities">
          {ENTITIES.map((entity) => (
            <label key={entity.id} className="adm-tag-entity">
              <input
                type="checkbox"
                checked={newGroup.entity_types.includes(entity.id)}
                onChange={() =>
                  setNewGroup((prev) => ({
                    ...prev,
                    entity_types: prev.entity_types.includes(entity.id)
                      ? prev.entity_types.filter((x) => x !== entity.id)
                      : [...prev.entity_types, entity.id],
                  }))
                }
              />
              {entity.label}
            </label>
          ))}
        </span>
        <button
          className="oc-btn oc-btn-primary"
          type="submit"
          disabled={!newGroup.label.trim() || newGroup.entity_types.length === 0}
        >
          Add group
        </button>
      </form>

      {loading && <p className="apr-muted">Loading…</p>}

      {!loading && groups.length === 0 && (
        <div className="oc-empty">
          <div className="oc-empty-title">No tag groups yet</div>
          <div className="oc-empty-sub">Create the first dimension above, then add its values.</div>
        </div>
      )}

      {groups.map((group) => (
        <section className="adm-tag-group" key={group.id}>
          <div className="adm-tag-head">
            <div>
              <h2 className="adm-tag-title">{group.label}</h2>
              <p className="apr-muted adm-tag-sub">
                <code>{group.code}</code> · labels{' '}
                {(group.entity_types || []).map(entityLabel).join(' and ').toLowerCase() || 'nothing'}
              </p>
            </div>
            <div className="adm-actions">
              <button type="button" className="oc-btn" onClick={() => removeGroup(group)}>
                Delete group
              </button>
            </div>
          </div>

          <div className="oc-table-wrap">
            <table className="oc-table">
              <thead>
                <tr>
                  <th>Tag</th>
                  <th>Code</th>
                  <th>Customers</th>
                  <th>Products</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(group.values || []).map((value) => (
                  <tr key={value.id}>
                    <td>{value.label}</td>
                    <td className="apr-sku">{value.code}</td>
                    <td>{(group.entity_types || []).includes('customer') ? Number(value.customer_count || 0) : '—'}</td>
                    <td>{(group.entity_types || []).includes('product') ? Number(value.product_count || 0) : '—'}</td>
                    <td className="adm-actions">
                      <button type="button" className="oc-btn" onClick={() => removeValue(group, value)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {(group.values || []).length === 0 && (
                  <tr>
                    <td colSpan={5} className="apr-muted">
                      No values yet — nothing can be tagged with this group until you add one.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <form className="adm-newrow adm-rulerow" onSubmit={(e) => addValue(group, e)}>
            <input
              className="oc-input"
              placeholder="Tag name, e.g. Swimart"
              value={(newValue[group.id] || {}).label || ''}
              onChange={(e) =>
                setNewValue((prev) => ({
                  ...prev,
                  [group.id]: { ...(prev[group.id] || {}), label: e.target.value },
                }))
              }
            />
            <input
              className="oc-input"
              placeholder={slug((newValue[group.id] || {}).label) || 'code (auto)'}
              value={(newValue[group.id] || {}).code || ''}
              onChange={(e) =>
                setNewValue((prev) => ({
                  ...prev,
                  [group.id]: { ...(prev[group.id] || {}), code: e.target.value },
                }))
              }
            />
            <button
              className="oc-btn oc-btn-primary"
              type="submit"
              disabled={!((newValue[group.id] || {}).label || '').trim()}
            >
              Add tag
            </button>
          </form>
        </section>
      ))}

      {groups.length > 0 && <BulkTagPanel groups={groups} onDone={load} onError={setError} />}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────── */

function BulkTagPanel({ groups, onDone, onError }) {
  const [entityType, setEntityType] = useState('product');
  const [valueId, setValueId] = useState('');
  const [keys, setKeys] = useState('');
  const [remove, setRemove] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // Banner suggestions (customers only)
  const [bannerQuery, setBannerQuery] = useState('Swimart');
  const [candidates, setCandidates] = useState(null);
  const [candidateBusy, setCandidateBusy] = useState(false);

  const options = useMemo(
    () =>
      groups
        .filter((g) => (g.entity_types || []).includes(entityType))
        .flatMap((g) => (g.values || []).map((v) => ({ ...v, group: g }))),
    [groups, entityType],
  );

  // The selected tag has to be re-picked when the entity changes: a group that
  // only targets customers must not stay selected for a product run.
  useEffect(() => {
    if (valueId && !options.some((o) => String(o.id) === String(valueId))) setValueId('');
  }, [options, valueId]);

  const keyList = keys
    .split(/[\n,;\t]+/)
    .map((k) => k.trim())
    .filter(Boolean);

  const loadCandidates = async () => {
    setCandidateBusy(true);
    try {
      const data = await listCustomerTagCandidates(bannerQuery.trim());
      setCandidates(data.items || []);
    } catch (e) {
      onError(e?.response?.data?.detail || 'Could not search customers.');
    } finally {
      setCandidateBusy(false);
    }
  };

  const addCandidates = () => {
    const lines = (candidates || []).map((c) => `${c.company}/${c.cust_num}`);
    setKeys((prev) => [...new Set([...keyList, ...lines])].join('\n'));
  };

  const apply = async () => {
    if (!valueId || keyList.length === 0 || busy) return;
    setBusy(true);
    setResult(null);
    try {
      const res = await bulkAssignTag({
        entity_type: entityType,
        tag_value_id: Number(valueId),
        keys: keyList,
        remove,
      });
      setResult(res);
      onDone();
    } catch (e) {
      onError(e?.response?.data?.detail || 'Bulk tagging failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="adm-tag-group">
      <div className="adm-tag-head">
        <div>
          <h2 className="adm-tag-title">Tag a list</h2>
          <p className="apr-muted adm-tag-sub">
            Paste SKUs, or customer keys as <code>AU/51234</code>. Keys that are not entities here
            are reported back rather than silently skipped.
          </p>
        </div>
      </div>

      <div className="adm-newrow adm-rulerow">
        <select
          className="oc-input"
          value={entityType}
          onChange={(e) => { setEntityType(e.target.value); setResult(null); }}
        >
          {ENTITIES.map((entity) => (
            <option key={entity.id} value={entity.id}>{entity.label}</option>
          ))}
        </select>
        <select className="oc-input" value={valueId} onChange={(e) => setValueId(e.target.value)}>
          <option value="">Tag…</option>
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.group.label} · {option.label}
            </option>
          ))}
        </select>
        <label className="adm-tag-entity">
          <input type="checkbox" checked={remove} onChange={(e) => setRemove(e.target.checked)} />
          Remove instead of add
        </label>
        <button
          type="button"
          className="oc-btn oc-btn-primary"
          onClick={apply}
          disabled={busy || !valueId || keyList.length === 0}
        >
          {busy ? 'Working…' : `${remove ? 'Untag' : 'Tag'} ${keyList.length} ${entityType === 'product' ? 'SKU(s)' : 'customer(s)'}`}
        </button>
      </div>

      {entityType === 'customer' && (
        <div className="adm-newrow adm-rulerow">
          <input
            className="oc-input"
            placeholder="Find customers by banner group or name"
            value={bannerQuery}
            onChange={(e) => setBannerQuery(e.target.value)}
          />
          <button
            type="button"
            className="oc-btn"
            onClick={loadCandidates}
            disabled={candidateBusy || bannerQuery.trim().length < 2}
          >
            {candidateBusy ? 'Searching…' : 'Search'}
          </button>
          {candidates && (
            <>
              <span className="apr-muted">
                {candidates.length} match{candidates.length === 1 ? '' : 'es'} on the Epicor banner
                group or name — check them before tagging.
              </span>
              <button type="button" className="oc-btn" onClick={addCandidates} disabled={candidates.length === 0}>
                Add to list
              </button>
            </>
          )}
        </div>
      )}

      {candidates && candidates.length > 0 && (
        <div className="oc-table-wrap adm-tag-candidates">
          <table className="oc-table">
            <thead>
              <tr>
                <th>Key</th>
                <th>Customer</th>
                <th>Banner group</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {candidates.map((c) => (
                <tr key={`${c.company}-${c.cust_num}`}>
                  <td className="apr-sku">{c.company}/{c.cust_num}</td>
                  <td>{c.cust_id ? `${c.cust_id} — ${c.name || ''}` : c.name || '—'}</td>
                  <td>{c.sc6_banner_group || <span className="apr-muted">none</span>}</td>
                  <td>{c.inactive ? 'Inactive' : 'Active'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="adm-tag-keys">
        <textarea
          className="oc-input"
          rows={8}
          placeholder={entityType === 'product' ? 'DCLG-15C\nDNP15C\n…' : 'AU/51234\nNZ/2001\n…'}
          value={keys}
          onChange={(e) => setKeys(e.target.value)}
        />
      </div>

      {result && (
        <div className={result.missing?.length ? 'apr-alert warn' : 'apr-alert ok'}>
          {result.changed} {remove ? 'removed' : 'tagged'} of {result.matched} matched
          {result.already ? ` · ${result.already} already carried the tag` : ''}
          {result.missing?.length ? (
            <>
              {' '}· {result.missing.length} not found:{' '}
              <span className="apr-sku">{result.missing.slice(0, 40).join(', ')}</span>
              {result.missing.length > 40 ? ' …' : ''}
            </>
          ) : null}
        </div>
      )}
    </section>
  );
}
